import type { Film } from '@/types/film';
import { films } from '@/mocks/films';

const byNewest = (a: Film, b: Film) => (a.updatedAt < b.updatedAt ? 1 : -1);

const queryShows = [
  '剧情',
  '悬疑',
  '爱情',
  '科幻',
  '动作',
  '喜剧',
  '动画',
  '犯罪',
  '古装',
  '都市',
  '战争',
  '家庭',
  '青春',
  '职场',
  '深海',
  '时间',
  '城市',
  '热血',
];

export const getFilmById = (id: string | undefined): Film | undefined =>
  id ? films.find((film) => film.id === id) : undefined;

export const getByType = (type: string): Film[] => films.filter((film) => film.type === type).sort(byNewest);

export const getLatestAll = (limit = 8): Film[] => [...films].sort(byNewest).slice(0, limit);

export const getTrending = (limit = 8): Film[] => [...films].sort((a, b) => b.views - a.views).slice(0, limit);

export const getHighlighted = (limit = 5): Film[] => [...films].sort((a, b) => b.views - a.views).slice(0, limit);

export interface FilmQuery {
  type?: string;
  genre?: string;
  region?: string;
  year?: string;
  sort?: 'updated' | 'hot' | 'title';
  keyword?: string;
}

export const sortFilms = (list: Film[], sort: FilmQuery['sort'] = 'updated'): Film[] => {
  const copy = [...list];
  if (sort === 'hot') return copy.sort((a, b) => b.views - a.views);
  if (sort === 'title') return copy.sort((a, b) => a.title.localeCompare(b.title, 'zh-Hans-CN'));
  return copy.sort(byNewest);
};

export const filterFilms = (query: FilmQuery): Film[] => {
  const keyword = (query.keyword ?? '').trim().toLowerCase();
  const result = films.filter((film) => {
    if (query.type && film.type !== query.type) return false;
    if (query.genre && film.genre !== query.genre) return false;
    if (query.region && film.region !== query.region) return false;
    if (query.year && String(film.year) !== query.year) return false;
    if (keyword) {
      const haystack = [film.title, film.genre, film.region, film.summary, ...film.tags].join(' ').toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    return true;
  });
  return sortFilms(result, query.sort);
};

export const searchFilms = (keyword: string): Film[] => {
  const word = keyword.trim().toLowerCase();
  if (!word) return [];
  const matched = films.filter((film) => {
    const haystack = [film.title, film.genre, film.region, film.summary, film.director, ...film.tags, ...film.cast]
      .join(' ')
      .toLowerCase();
    return haystack.includes(word);
  });
  return sortFilms(matched, 'hot');
};

export const getRelated = (film: Film, limit = 4): Film[] => {
  const scored = films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      let score = 0;
      if (item.type === film.type) score += 3;
      if (item.genre === film.genre) score += 4;
      if (item.region === film.region) score += 2;
      score += item.tags.filter((tag) => film.tags.includes(tag)).length * 2;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);
  return scored.slice(0, limit).map((entry) => entry.item);
};

export const availableYears = Array.from(
  new Set(films.map((film) => film.year)),
).sort((a, b) => b - a);

export const popularSearches = queryShows.slice(0, 8);

export const viewCountLabel = (views: number): string => {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万次观看`;
  return `${views} 次观看`;
};